import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { Wordmark } from "@/components/Header";
import { services } from "@/data/services";

const SOCIALS = [
  { name: "LinkedIn", href: "https://www.linkedin.com/company/umi-it-services", Icon: Linkedin, testid: "footer-social-linkedin" },
  { name: "Facebook", href: "https://www.facebook.com/umiitservices", Icon: Facebook, testid: "footer-social-facebook" },
  { name: "Instagram", href: "https://www.instagram.com/umiitservices", Icon: Instagram, testid: "footer-social-instagram" },
];

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#07182E] text-slate-300 py-16 border-t border-slate-800">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Link to="/" aria-label="UMI IT Services home"><Wordmark /></Link>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Enterprise-grade IT support with small-business warmth, proudly based in Adelaide, South Australia.
          </p>
          <p className="mt-4 text-sm text-slate-400 flex items-start gap-2">
            <MapPin size={16} className="mt-0.5 shrink-0 text-[#17B8A6]" aria-hidden="true" />
            <span>Serving the Adelaide CBD, North Adelaide, Norwood, Unley, Mawson Lakes, West Lakes, Marion and greater metro SA.</span>
          </p>
          <div className="mt-5 flex items-center gap-3" data-testid="footer-social-links">
            {SOCIALS.filter((s) => s.href).map(({ name, href, Icon, testid }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener"
                aria-label={`UMI IT Services on ${name}`}
                data-testid={testid}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-700 text-slate-400 hover:text-[#17B8A6] hover:border-[#17B8A6] transition-colors duration-150"
              >
                <Icon size={16} aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-white font-heading font-semibold text-sm uppercase tracking-wider">Services</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {services.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="hover:text-[#17B8A6] transition-colors duration-150">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-white font-heading font-semibold text-sm uppercase tracking-wider">Company</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link to="/about" data-testid="footer-link-about" className="hover:text-[#17B8A6] transition-colors duration-150">About UMI</Link></li>
            <li><Link to="/regulated-industries" data-testid="footer-link-regulated" className="hover:text-[#17B8A6] transition-colors duration-150">Regulated Industries</Link></li>
            <li><Link to="/blog" data-testid="footer-link-blog" className="hover:text-[#17B8A6] transition-colors duration-150">Blog &amp; Guides</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#17B8A6] transition-colors duration-150">Contact</Link></li>
            <li><Link to="/contact" data-testid="footer-cta-consultation" className="hover:text-[#17B8A6] transition-colors duration-150">Book a Free Consultation</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-white font-heading font-semibold text-sm uppercase tracking-wider">Contact</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li>
              <a href="tel:+61489222864" data-testid="footer-phone-link" className="flex items-center gap-2 hover:text-[#17B8A6] transition-colors duration-150">
                <Phone size={16} className="shrink-0 text-[#17B8A6]" aria-hidden="true" />
                0489 222 864
              </a>
            </li>
            <li>
              <a href="mailto:maneesh@umi-it.com.au" data-testid="footer-email-link" className="flex items-center gap-2 hover:text-[#17B8A6] transition-colors duration-150">
                <Mail size={16} className="shrink-0 text-[#17B8A6]" aria-hidden="true" />
                maneesh@umi-it.com.au
              </a>
            </li>
            <li className="text-slate-400">Servicing Greater Adelaide</li>
          </ul>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-800 text-xs text-slate-500">
        <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
          <Link to="/privacy-policy" data-testid="footer-link-privacy" className="hover:text-[#17B8A6] transition-colors duration-150">Privacy Policy</Link>
          <Link to="/terms-of-use" data-testid="footer-link-terms" className="hover:text-[#17B8A6] transition-colors duration-150">Terms of Use</Link>
          <Link to="/cookie-policy" data-testid="footer-link-cookies" className="hover:text-[#17B8A6] transition-colors duration-150">Cookie Policy</Link>
          <Link to="/data-protection" data-testid="footer-link-data-protection" className="hover:text-[#17B8A6] transition-colors duration-150">Data Protection &amp; Security</Link>
          <button data-testid="footer-cookie-settings" onClick={() => window.dispatchEvent(new Event("umi:open-cookie-settings"))} className="hover:text-[#17B8A6] transition-colors duration-150">Cookie Settings</button>
        </nav>
        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <p data-testid="footer-copyright-text">© {year} UMI IT Services. All rights reserved.</p>
          <p data-testid="footer-legal-text">UMI IT Services is a trading name of Breakfree Ventures Pty Ltd ATF the Shah Family Trust · ABN 91 864 834 188</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
